// src/components/Sessions.js - Updated with therapist view support
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon.js';
import VoiceInput from './VoiceInput.js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Sessions = React.memo(({ token, patientId = null, isTherapistView = false, selectedPatient = null }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState(new Set()); // Track expanded sessions
  const [editingSession, setEditingSession] = useState(null); // Track session being edited
  const [editFormData, setEditFormData] = useState({
    date: '',
    notes: '',
    transcript: '',
    session_type: 'in_person'
  });
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
    notes: '',
    transcript: '',
    session_type: 'in_person' // Default to in-person
  });

  useEffect(() => {
    if (token && (patientId || !isTherapistView)) {
      loadSessions();
    }
  }, [token, patientId, isTherapistView]); // Optimized dependencies

  // Debug: Monitor form data changes
  // useEffect(() => {
  //   console.log('🔍 Form data changed:', formData);
  // }, [formData]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading sessions...', { isTherapistView, patientId });
      
      let url = `${API_BASE}/api/sessions/sessions`;
      if (isTherapistView && patientId) {
        url += `?patient_id=${patientId}&limit=50`;  // Load more sessions for therapist view
      } else if (isTherapistView) {
        url += `?limit=50`;  // Load more sessions for therapist view
      }
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Sessions loaded:', response.data);
      setSessions(response.data);
    } catch (error) {
      console.error('❌ Error loading sessions:', error);
      
      // Check for session expiration
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Redirect to login
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  };

  // Voice input handlers
  const handleNotesVoiceInput = (transcript) => {
    console.log('🎤 Voice input for notes:', transcript);
    console.log('🔍 Current formData before update:', formData);
    
    setFormData(prev => {
      console.log('🔍 Previous formData in setter:', prev);
      const newNotes = prev.notes ? `${prev.notes} ${transcript}` : transcript;
      const newData = {
        date: prev.date,
        notes: newNotes,
        transcript: prev.transcript,
        session_type: prev.session_type
      };
      console.log('📝 New formData after notes update:', newData);
      return newData;
    });
  };

  const handleTranscriptVoiceInput = (transcript) => {
    console.log('🎤 Voice input for transcript:', transcript);
    console.log('🔍 Current formData before update:', formData);
    
    setFormData(prev => {
      console.log('🔍 Previous formData in setter:', prev);
      const newTranscript = prev.transcript ? `${prev.transcript} ${transcript}` : transcript;
      const newData = {
        date: prev.date,
        notes: prev.notes,
        transcript: newTranscript,
        session_type: prev.session_type
      };
      console.log('📝 New formData after transcript update:', newData);
      return newData;
    });
  };

  const createSession = async (e) => {
    e.preventDefault();
    
    console.log('📋 Current form data before submission:', formData);
    
    // Only allow session creation for non-therapist view or when therapist is working with specific patient
    if (isTherapistView && !patientId) {
      alert('Kan inte skapa session utan vald klient');
      return;
    }
    
    setLoading(true);
    
    try {
      let requestData = { ...formData };
      
      // If therapist is creating session for patient, include patient_id
      if (isTherapistView && patientId) {
        requestData.patient_id = patientId;
      }
      
      console.log('📝 Creating session with data:', requestData);
      
      await axios.post(`${API_BASE}/api/sessions/sessions`, requestData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Reset form and reload sessions
      setFormData({
        date: new Date().toISOString().slice(0, 16),
        notes: '',
        transcript: '',
        session_type: 'in_person'
      });
      setShowForm(false);
      loadSessions();
    } catch (error) {
      console.error('❌ Error creating session:', error);
      alert('Kunde inte skapa session: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const startEditSession = (session) => {
    console.log('✏️ Starting to edit session:', session.id);
    setEditingSession(session.id);
    setEditFormData({
      date: session.date.slice(0, 16), // Convert to datetime-local format
      notes: session.notes || '',
      transcript: session.transcript || '',
      session_type: session.session_type || 'in_person'
    });
  };

  const cancelEditSession = () => {
    console.log('❌ Canceling session edit');
    setEditingSession(null);
    setEditFormData({
      date: '',
      notes: '',
      transcript: '',
      session_type: 'in_person'
    });
  };

  const saveEditSession = async (sessionId) => {
    console.log('💾 Saving session edit:', sessionId, editFormData);
    
    if (!editFormData.notes.trim()) {
      alert('Sessionsanteckningar är obligatoriska');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API_BASE}/api/sessions/sessions/${sessionId}`, editFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Session updated successfully');
      
      // Reset edit state and reload sessions
      setEditingSession(null);
      setEditFormData({
        date: '',
        notes: '',
        transcript: '',
        session_type: 'in_person'
      });
      loadSessions();
    } catch (error) {
      console.error('❌ Error updating session:', error);
      alert('Kunde inte uppdatera session: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Voice input handlers for edit form
  const handleEditNotesVoiceInput = (transcript) => {
    console.log('🎤 Voice input for edit notes:', transcript);
    setEditFormData(prev => ({
      ...prev,
      notes: prev.notes ? `${prev.notes} ${transcript}` : transcript
    }));
  };

  const handleEditTranscriptVoiceInput = (transcript) => {
    console.log('🎤 Voice input for edit transcript:', transcript);
    setEditFormData(prev => ({
      ...prev,
      transcript: prev.transcript ? `${prev.transcript} ${transcript}` : transcript
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      full: date.toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      weekday: date.toLocaleDateString('sv-SE', { weekday: 'long' }),
      dayMonth: date.toLocaleDateString('sv-SE', { 
        day: 'numeric', 
        month: 'short' 
      }),
      year: date.getFullYear(),
      time: date.toLocaleTimeString('sv-SE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  const getSessionTitle = () => {
    if (isTherapistView && patientId) {
      return 'Klientens Terapisessioner';
    }
    return 'Terapisessioner';
  };

  const getCreateButtonText = () => {
    if (isTherapistView) {
      return 'Ny Session';
    }
    return 'Ny Session';
  };

  const getSessionTypeInfo = (sessionType) => {
    const types = {
      'in_person': {
        label: 'Personligt möte',
        icon: 'armchair',
        description: 'Besök på mottagningen'
      },
      'video_call': {
        label: 'Videomöte',
        icon: 'video',
        description: 'Online via video'
      },
      'phone_call': {
        label: 'Telefonsamtal',
        icon: 'phone',
        description: 'Samtal via telefon'
      },
      'home_visit': {
        label: 'Hembesök',
        icon: 'home',
        description: 'Besök hemma hos klient'
      }
    };
    
    return types[sessionType] || types['in_person'];
  };

  const parseInsights = (insights) => {
    if (typeof insights === 'string') {
      // Try to parse structured text into sections
      const sections = insights.split(/\n\s*\n/);
      return sections.map((section, index) => {
        const lines = section.trim().split('\n');
        if (lines.length === 0) return null;
        
        const firstLine = lines[0].trim();
        const isHeader = firstLine.includes(':') || firstLine.match(/^[A-ZÅÄÖ]/);
        
        if (isHeader && lines.length > 1) {
          const header = firstLine.replace(':', '');
          const bulletPoints = lines.slice(1)
            .filter(line => line.trim())
            .map(line => line.replace(/^[-•*]\s*/, '').trim());
          
          return (
            <div key={index} className="mb-4">
              <h6 className="font-semibold text-blue-900 mb-2">{header}</h6>
              <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                {bulletPoints.map((point, pointIndex) => (
                  <li key={pointIndex}>{point}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          return (
            <p key={index} className="text-blue-700 text-sm mb-2">
              {firstLine}
            </p>
          );
        }
      }).filter(Boolean);
    }
    
    if (typeof insights === 'object' && insights !== null) {
      // Handle structured object insights
      return Object.entries(insights).map(([key, value], index) => {
        if (key === 'error') return null;
        
        const header = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        if (Array.isArray(value)) {
          return (
            <div key={index} className="mb-4">
              <h6 className="font-semibold text-blue-900 mb-2">{header}</h6>
              <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                {value.map((item, itemIndex) => (
                  <li key={itemIndex}>{typeof item === 'object' ? JSON.stringify(item) : item}</li>
                ))}
              </ul>
            </div>
          );
        } else if (typeof value === 'string') {
          // Check if value contains bullet points or numbered lists
          const bulletPattern = /^[-•*]\s+/gm;
          const numberedPattern = /^\d+\.\s+/gm;
          
          if (bulletPattern.test(value) || numberedPattern.test(value)) {
            const points = value.split('\n')
              .filter(line => line.trim())
              .map(line => line.replace(/^[-•*]\s*|\d+\.\s*/, '').trim());
            
            return (
              <div key={index} className="mb-4">
                <h6 className="font-semibold text-blue-900 mb-2">{header}</h6>
                <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                  {points.map((point, pointIndex) => (
                    <li key={pointIndex}>{point}</li>
                  ))}
                </ul>
              </div>
            );
          } else {
            return (
              <div key={index} className="mb-4">
                <h6 className="font-semibold text-blue-900 mb-2">{header}</h6>
                <p className="text-blue-700 text-sm">{value}</p>
              </div>
            );
          }
        } else {
          return (
            <div key={index} className="mb-4">
              <h6 className="font-semibold text-blue-900 mb-2">{header}</h6>
              <p className="text-blue-700 text-sm">{JSON.stringify(value)}</p>
            </div>
          );
        }
      }).filter(Boolean);
    }
    
    return <p className="text-blue-700 text-sm">Okänt format för insights</p>;
  };

  // Add navigation breadcrumb for therapist view
  const renderBreadcrumb = () => {
    if (!isTherapistView || !selectedPatient) return null;

    return (
      <div className="">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <button
                onClick={() => navigate('/therapist')}
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                <Icon name="users" size={16} className="mr-2" />
                Klienter
              </button>
            </li>
            <li>
              <div className="flex items-center">
                <Icon name="chevron-right" size={16} className="text-gray-400" />
                <button
                  onClick={() => navigate(`/therapist/client/${selectedPatient.id}`)}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {selectedPatient.name}
                </button>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <Icon name="chevron-right" size={16} className="text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Sessioner</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>
    );
  };

  const toggleSessionExpansion = (sessionId) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const isSessionExpanded = (sessionId) => {
    return expandedSessions.has(sessionId);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Add breadcrumb for therapist view */}
      {isTherapistView && renderBreadcrumb()}
      
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          {isTherapistView ? `${selectedPatient?.name || 'Klient'} - Sessioner` : 'Terapisessioner'}
        </h2>
        
        {isTherapistView && (
          <button 
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            disabled={loading}
          >
            {showForm ? 'Avbryt' : getCreateButtonText()}
          </button>
        )}
      </div>

      {showForm &&  (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Skapa ny session {isTherapistView ? 'för klient' : ''}</h3>
          
          <form onSubmit={createSession} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Datum och tid:</label>
              <input
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Sessionstyp:</label>
              <div className="text-xs text-gray-500 mb-2">Aktuellt val: {getSessionTypeInfo(formData.session_type).label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['in_person', 'video_call', 'phone_call', 'home_visit'].map((type) => {
                  const typeInfo = getSessionTypeInfo(type);
                  return (
                    <label key={type} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="session_type"
                        value={type}
                        checked={formData.session_type === type}
                        onChange={(e) => setFormData({...formData, session_type: e.target.value})}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <Icon name={typeInfo.icon} size={20} className="text-blue-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{typeInfo.label}</div>
                        <div className="text-xs text-gray-500">{typeInfo.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Sessionsanteckningar:</label>
              <div className="space-y-3">
                <VoiceInput 
                  onTranscript={handleNotesVoiceInput}
                  disabled={loading}
                  placeholder="Klicka på mikrofonen för att diktera anteckningar..."
                />
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={isTherapistView 
                    ? "Beskriv sessionen med klienten, tekniker som användes, framsteg, etc. Du kan också använda röstinmatning ovan..."
                    : "Beskriv vad som diskuterades, tekniker som användes, framsteg, etc. Du kan också använda röstinmatning ovan..."
                  }
                  rows="6"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Transkript (valfritt):</label>
              <div className="space-y-3">
                <VoiceInput 
                  onTranscript={handleTranscriptVoiceInput}
                  disabled={loading}
                  placeholder="Klicka på mikrofonen för att diktera transkript..."
                />
                
                <textarea
                  value={formData.transcript}
                  onChange={(e) => setFormData({...formData, transcript: e.target.value})}
                  placeholder="Fullständig utskrift av sessionen. Du kan också använda röstinmatning ovan..."
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button 
                type="submit" 
                disabled={loading}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Sparar...' : 'Spara Session'}
              </button>
              <button 
                type="button" 
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {loading && (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Laddar sessioner...</p>
          </div>
        )}
        
        {sessions.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-600">
              {isTherapistView 
                ? 'Inga sessioner registrerade för denna klient än.' 
                : 'Inga sessioner ännu. Skapa din första session!'
              }
            </p>
          </div>
        )}

        {sessions
          .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date descending
          .map((session, index) => {
          const isExpanded = isSessionExpanded(session.id);
          const dateInfo = formatDate(session.date);
          
          // Check if this is the first session of the year
          const currentYear = new Date(session.date).getFullYear();
          const sortedSessions = sessions.sort((a, b) => new Date(b.date) - new Date(a.date));
          const previousSession = index > 0 ? sortedSessions[index - 1] : null;
          const previousYear = previousSession ? new Date(previousSession.date).getFullYear() : null;
          const showYear = previousYear !== currentYear;
          
          return (
            <React.Fragment key={session.id}>
              {/* Year Header */}
              {showYear && (
                <div className="flex items-center my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <h3 className="px-4 text-lg font-bold text-gray-600 bg-white">
                    {currentYear}
                  </h3>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
              )}
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Collapsible Header */}
              <div 
                className="flex justify-between items-center p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleSessionExpansion(session.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Icon name="calendar" size={20} className="text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    {/* Mobile layout */}
                    <div className="block md:hidden">
                      <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <h4 className="text-lg font-bold text-gray-900">
                            {dateInfo.dayMonth}
                          </h4>
                        {session.therapist_name && (
                          <span className="flex gap-1 text-xs text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">
                            <Icon name="message-circle-heart"/> 
                            <span>{session.therapist_name}</span>
                          </span>
                        )}
                        <span className="flex gap-1 items-center text-xs text-green-600 font-medium bg-green-50 px-1.5 py-0.5 rounded">
                          <Icon name={getSessionTypeInfo(session.session_type).icon} />
                          <span>{getSessionTypeInfo(session.session_type).label}</span>
                        </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <span className="capitalize">{dateInfo.weekday}</span>
                          <span>•</span>
                          <span>{dateInfo.time}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Desktop layout */}
                    <div className="hidden md:block">
                      <div className="flex gap-3 items-center">
                        <h4 className="text-lg font-bold text-gray-900">
                          {dateInfo.dayMonth}
                        </h4>
                        <span className="text-sm text-gray-600 capitalize font-medium">
                          {dateInfo.weekday}
                        </span>
                        <span className="text-sm text-gray-500">
                          {dateInfo.time}
                        </span>
                        {session.therapist_name && (
                          <span className="flex gap-1 text-xs text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">
                              <Icon name="message-circle-heart"/> <span>{session.therapist_name}</span>
                            </span>
                        )}
                        <span className="flex gap-1 text-xs items-center text-green-600 font-medium bg-green-50 px-1.5 py-0.5 rounded">
                          <Icon name={getSessionTypeInfo(session.session_type).icon} />
                          <span>{getSessionTypeInfo(session.session_type).label}</span>
                        </span>
                      </div>
                      {/* Only show notes preview for therapists */}
                      {isTherapistView && session.notes && (
                        <p className="text-sm text-gray-600 truncate max-w-2xl mt-1">
                          {session.notes.length > 100 ? `${session.notes.substring(0, 100)}...` : session.notes}
                        </p>
                      )}
                      {/* Show simple confirmation for clients */}
                      {!isTherapistView && (
                        <p className="text-sm text-gray-500 mt-1">
                          Session registrerad
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                  {/* ID - hidden on mobile, visible on desktop */}
                  {/* <span className="hidden md:block text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                    <Icon name="info" size={12} />
                    ID: {session.id.slice(-8)}
                  </span> */}
                  
                  {/* Animated Chevron - always visible */}
                  <Icon 
                    name="chevron-right" 
                    size={20} 
                    className={`text-gray-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                  />
                </div>
              </div>
              
              {/* Expandable Content */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="space-y-4 pt-4">
                    
                    {/* Session metadata - show for both patient and therapist */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-700 flex items-center gap-2">
                            <Icon name={getSessionTypeInfo(session.session_type).icon} size={16} className="text-blue-600" />
                            <strong className="font-semibold">Typ:</strong> {getSessionTypeInfo(session.session_type).label}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">
                            <strong className="font-semibold">Skapad:</strong> {formatDate(session.created_at).full}
                          </p>
                        </div>
                        {session.therapist_name && (
                          <div>
                            <p className="text-sm text-gray-700">
                              <strong className="font-semibold">Terapeut:</strong> {session.therapist_name}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Client view - only show basic session confirmation */}
                    {!isTherapistView && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <Icon name="check-circle" size={20} className="text-blue-600" />
                          <div>
                            <h5 className="text-sm font-semibold text-blue-800">Session registrerad</h5>
                            <p className="text-blue-700 text-sm">
                              Din terapisession har registrerats och din terapeut har tillgång till detaljerna.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Therapist view - show detailed session information */}
                    {isTherapistView && session.notes && editingSession !== session.id && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Icon name="file-text" size={16} />
                            Fullständiga anteckningar:
                          </h5>
                          <button
                            onClick={() => startEditSession(session)}
                            className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm font-medium"
                          >
                            <Icon name="pencil" size={14} />
                            Redigera
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{session.notes}</p>
                        </div>
                      </div>
                    )}

                    {/* Edit form for session */}
                    {isTherapistView && editingSession === session.id && (
                      <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <div className="flex justify-between items-center">
                          <h5 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                            <Icon name="pencil" size={16} />
                            Redigera session
                          </h5>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEditSession(session.id)}
                              disabled={loading}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
                            >
                              <Icon name="check" size={16} />
                              {loading ? 'Sparar...' : 'Spara'}
                            </button>
                            <button
                              onClick={cancelEditSession}
                              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-2 font-medium"
                            >
                              <Icon name="x" size={16} />
                              Avbryt
                            </button>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Datum och tid:</label>
                            <input
                              type="datetime-local"
                              value={editFormData.date}
                              onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Sessionstyp:</label>
                            <div className="text-xs text-gray-500 mb-2">Aktuellt val: {getSessionTypeInfo(editFormData.session_type).label}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {['in_person', 'video_call', 'phone_call', 'home_visit'].map((type) => {
                                const typeInfo = getSessionTypeInfo(type);
                                return (
                                  <label key={type} className="flex items-center space-x-3 p-3 border bg-white border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="edit_session_type"
                                      value={type}
                                      checked={editFormData.session_type === type}
                                      onChange={(e) => setEditFormData({...editFormData, session_type: e.target.value})}
                                      className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <Icon name={typeInfo.icon} size={20} className="text-blue-600" />
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">{typeInfo.label}</div>
                                      <div className="text-xs text-gray-500">{typeInfo.description}</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Sessionsanteckningar:</label>
                            <div className="space-y-3">
                              <VoiceInput 
                                onTranscript={handleEditNotesVoiceInput}
                                disabled={loading}
                                placeholder="Klicka på mikrofonen för att lägga till anteckningar..."
                              />
                              <textarea
                                value={editFormData.notes}
                                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                                placeholder="Beskriv sessionen med klienten, tekniker som användes, framsteg, etc..."
                                rows="6"
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Transkript (valfritt):</label>
                            <div className="space-y-3">
                              <VoiceInput 
                                onTranscript={handleEditTranscriptVoiceInput}
                                disabled={loading}
                                placeholder="Klicka på mikrofonen för att lägga till transkript..."
                              />
                              <textarea
                                value={editFormData.transcript}
                                onChange={(e) => setEditFormData({...editFormData, transcript: e.target.value})}
                                placeholder="Fullständig utskrift av sessionen..."
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                              />
                            </div>
                          </div>

                          <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                            <Icon name="info" size={12} className="inline mr-1" />
                            AI-analys kommer att köras automatiskt när du sparar ändringarna.
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {isTherapistView && session.key_insights && !session.key_insights.error && editingSession !== session.id && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                          <Icon name="lightbulb" size={16} />
                          AI-analys:
                          {session.key_insights.mock_analysis && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Demo-läge
                            </span>
                          )}
                          {session.key_insights.fallback_analysis && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                              Begränsad analys
                            </span>
                          )}
                          {session.key_insights.parse_error && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              Analys-fel
                            </span>
                          )}
                        </h5>
                        <div className="space-y-2">
                          {parseInsights(session.key_insights)}
                        </div>
                        {session.key_insights.note && (
                          <div className="mt-3 text-xs text-blue-600 bg-blue-100 p-2 rounded">
                            <Icon name="info" size={12} className="inline mr-1" />
                            {session.key_insights.note}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Loading indicator during session edit save */}
                    {isTherapistView && editingSession === session.id && loading && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 border-2 border-yellow-300 border-t-yellow-600 rounded-full animate-spin"></div>
                          <div>
                            <h5 className="text-sm font-semibold text-yellow-800">Sparar ändringar...</h5>
                            <p className="text-yellow-700 text-sm">
                              Session uppdateras och AI-analys körs automatiskt.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {isTherapistView && session.key_insights && session.key_insights.error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                        <h5 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                          <Icon name="alert-triangle" size={16} />
                          AI-analys:
                        </h5>
                        <p className="text-red-700 text-sm">
                          {session.key_insights.fallback_analysis 
                            ? "AI-analys inte tillgänglig - grundläggande analys visas"
                            : "Kunde inte analysera sessionsanteckningar"
                          }
                        </p>
                        {session.key_insights.error && (
                          <details className="text-xs text-red-600">
                            <summary className="cursor-pointer">Teknisk information</summary>
                            <p className="mt-2 font-mono bg-red-100 p-2 rounded">{session.key_insights.error}</p>
                          </details>
                        )}
                      </div>
                    )}
                    
                    {/* Show transcript only in therapist view */}
                    {isTherapistView && session.transcript && editingSession !== session.id && (
                      <div className="border border-gray-200 rounded-lg">
                        <div className="bg-gray-50 p-3 border-b border-gray-200 flex justify-between items-center">
                          <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Icon name="file-text" size={16} />
                            Sessionstranskript
                          </h5>
                          <button
                            onClick={() => startEditSession(session)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm font-medium"
                          >
                            <Icon name="edit-3" size={14} />
                            Redigera
                          </button>
                        </div>
                        <div className="p-4 bg-white max-h-60 overflow-y-auto">
                          <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{session.transcript}</p>
                        </div>
                      </div>
                    )}

                    {/* Additional info for therapist view */}
                    {isTherapistView && (session.patient_summary || session.therapist_notes) && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        
                        {session.patient_summary && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <Icon name="user" size={14} />
                              Klientsammanfattning:
                            </h5>
                            <div className="bg-white rounded-md p-3 border border-gray-200">
                              <p className="text-gray-600 text-sm leading-relaxed">{session.patient_summary}</p>
                            </div>
                          </div>
                        )}
                        
                        {session.therapist_notes && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <Icon name="lock" size={14} />
                              Privata anteckningar:
                            </h5>
                            <div className="bg-white rounded-md p-3 border border-gray-200">
                              <p className="text-gray-600 text-sm leading-relaxed">{session.therapist_notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
});

export default Sessions;